import React, { useState, useEffect, useCallback, useContext } from "react";
import DataTable from "react-data-table-component";
import axiosInstance from "../axiosConfig";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
import "../assets/CSS/Dashboard.css";

function AdminTicketView() {
  const { user,  } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ fromDate: "", toDate: "" });
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update isMobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch tickets assigned to the logged-in supervisor using their EmployeeID.
  const fetchTickets = async () => {
    try {
      
      const response = await axiosInstance.get(`/api/tickets/byRole/Admin`);
      setTickets(response.data);
      setFilteredTickets(response.data);
    } catch (error) {
      console.error("Error fetching tickets for Admin:", error);
    }
  };

  useEffect(() => {
    if (user?.EmployeeID) {
      fetchTickets();
    }
  }, [user]);

  // Apply search and date filters.
  const applyFilters = useCallback(() => {
    const { fromDate, toDate } = filters;
    const filtered = tickets.filter((ticket) => {
      const ticketDate = ticket.CreatedDate ? new Date(ticket.CreatedDate) : null;
      const matchesSearch =
        searchText === "" ||
        [ticket.TicketID, ticket.Subject, ticket.TicketDescription, ticket.Worker_Name, ticket.Department]
          .join(" ")
          .toLowerCase()
          .includes(searchText.toLowerCase());
      const matchesDate =
        (!fromDate || (ticketDate && ticketDate >= new Date(fromDate))) &&
        (!toDate || (ticketDate && ticketDate <= new Date(toDate).setHours(23, 59, 59, 999)));
      return matchesSearch && matchesDate;
    });
    setFilteredTickets(filtered);
  }, [tickets, filters, searchText]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const showAlert = (message, type) => {
    const alertPlaceholder = document.getElementById("alertPlaceholder");
    const alertHTML = `
      <div class="alert alert-${type} alert-dismissible fade show" role="alert">
        <strong>${type === "success" ? "Success!" : "Error!"}</strong> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>`;
    alertPlaceholder.innerHTML = alertHTML;
    setTimeout(() => {
      alertPlaceholder.innerHTML = "";
    }, 3000);
  };

  // Function to handle status change.
  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      // Prepare the request body.
      // If the newStatus is "solved", include the Auth and SolvedBy.
      let requestBody = { TicketStatus: newStatus };
  
      if (newStatus.toLowerCase() === "solved") {
        requestBody.Auth = "Admin";
        requestBody.SolvedBy = user?.Name;
      }
  
      await axiosInstance.patch(
        `/api/User-tickets/${ticketId}/status`,
        requestBody
      );
  
      showAlert(`Status updated to "${newStatus}" successfully!`, "success");
      fetchTickets();
    } catch (error) {
      console.error("Error updating status:", error);
      showAlert("Failed to update status. Please try again!", "danger");
    }
  };

  const handleApproveNoWork = async (ticket) => {
    const result = await Swal.fire({
      title: "Approve 'No Work'?",
      text: "This will delete the Red Process and close this ticket.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Approve!"
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/api/approve-no-work/${ticket.TicketID}`, {
          data: { 
            SupervisorName: ticket.Supervisor_Name, 
            Department: ticket.Department 
          }
        });
        Swal.fire("Approved!", "No Work Approved. Records cleared.", "success");
        fetchTickets(); // List refresh karne ke liye
      } catch (error) {
        console.error("Error approving:", error);
        Swal.fire("Error!", "Failed to approve.", "error");
      }
    }
  };

  // Define columns for the data table.
  const columns = [
    {
      name: "Ticket ID",
      selector: (row) => row.TicketID,
      sortable: true,
      width: "80px"
    },
    {
      name: "Worker",
      selector: (row) => (
        <span
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={row.Worker_Name}
        >
          {row.Worker_Name}
        </span>
      ),
      sortable: true,
      width: "150px"
    },
    {
      name: "Department",
      selector: (row) => row.Department,
      sortable: true,
      width: "150px"
    },
    {
      name: "Supervisor Name",
      selector: (row) => (
        <span 
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={row.Supervisor_Name}
        > 
        {row.Supervisor_Name}

        </span>
      ),
      sortable: true
    },
    {
      name: "Subject",
      selector: (row) => (
        <span 
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={row.Subject}
        > 
        {row.Subject}

        </span>
      ),
      sortable: true,
      wrap: true
    },
    {
      name: "Description",
      selector: (row) =>(
        <span  
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={`${row.TicketDescription}`}>
          {row.TicketDescription}
        </span>       
      ),
      sortable: false,
      wrap: true,
      grow: 2
    },
    {
        name: "Status",
        cell: (row) => (
          <select
            value={row.TicketStatus}
            onChange={(e) => handleStatusChange(row.TicketID, e.target.value)}
            className="form-select form-select-sm bg-info"
         
          >
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Solved">Solved</option>
            {/* <option value="Closed">Closed</option> */}
          </select>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "150px"
      },
      {
        name: "Created Date",
        selector: (row) => 
          new Date(row.CreatedDate).toLocaleString("en-GB", {
            timeZone: "Asia/Kolkata",
            hour12: false, // Optional: use 24-hour format
          }),
        sortable: true,
        width: "200px" // Slightly wider for date + time
      }
  ];

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1">
        <Header toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} isSidebarVisible={isSidebarVisible} />
        <div id="alertPlaceholder"></div>
        <main className="main-container p-4" style={{height:"-webkit-fill-available"}}> 
          <div className="d-flex justify-content-between mb-3">
            <h2>Ticket Management</h2>
            <div className="d-flex align-items-center">
              <input
                type="text"
                className="form-control me-2"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: "auto" }}
              />
              <input
                type="date"
                className="form-control me-2"
                value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                style={{ width: "auto" }}
              />
              <input
                type="date"
                className="form-control me-2"
                value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                style={{ width: "auto" }}
              />
              <button className="btn btn-success" 
                onClick={() => window.location.reload()}
              >
                Reload ⟳
              </button>
            </div>
          </div>
          { !isMobile ? (
            <DataTable columns={columns} data={filteredTickets} pagination highlightOnHover pointerOnHover />
          ) : (
            <div className="table-mobile">
              {filteredTickets.map((ticket, idx) => (
                <div key={idx} className="card mb-2">
                  <div className="card-body">
                    <h5 className="card-title">Ticket #{ticket.TicketID}</h5>
                    <p className="card-text"><strong>Worker:</strong> {ticket.Worker_Name}</p>
                    <p className="card-text"><strong>Department:</strong> {ticket.Department}</p>
                    <p className="card-text"><strong>Subject:</strong> {ticket.Subject}</p>
                    <p className="card-text"><strong>Description:</strong> {ticket.TicketDescription}</p>
                    <p className="card-text"><strong>Created:</strong> {new Date(ticket.CreatedDate).toLocaleDateString("en-GB")}</p>
                    <div className="mb-2">
                      <label className="form-label"><strong>Status:</strong></label>
                      
                      {ticket.TicketStatus === "Pending Faisal Approval" ? (
                        <div>
                           <span className="badge bg-warning text-dark mb-2 d-block">Pending Faisal Approval</span>
                           <button 
                             className="btn btn-sm btn-success w-100" 
                             onClick={() => handleApproveNoWork(ticket)}
                           >
                             Approve 'No Work'
                           </button>
                        </div>
                      ) : (
                        // Normal tickets ke liye purana dropdown
                        <select
                          value={ticket.TicketStatus}
                          onChange={(e) => handleStatusChange(ticket.TicketID, e.target.value)}
                          className="form-select form-select-sm"
                        >
                          <option value="Open">Open</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Solved">Solved</option>
                          <option value="Closed">Closed</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminTicketView;
